import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTMLogContext } from "../../../context/domain/operations/TMLogContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  createTMPrice,
  updateTMPrice,
  getTMPricePdfUrl,
} from "../../../services/api/tmlog";
import {
  Button,
  SignaturePad,
  TooltipArrow,
  PdfViewerModal,
} from "../../../components/ui";
import {
  JobInformation,
  ReportInformation,
  WorkDetail,
  Comments,
  TMTable,
  AddLineItemButton,
} from "../../../features/operations/tmlog";
import SummaryTable from "../../../features/operations/tmlog/reportSections/SummaryTable";
import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { getScopesByWeek } from "../../../services/api/dispatch";
import {
  getWeekStart,
  getDateInLocalTZFromDateStr,
} from "../../../utils/datetime";
import { useFormHybrid } from "../../../utils/common/useFormHybrid";

/**
 * T&M Price Form Page
 * Add pricing to approved T&M reports
 *
 * IMPORTANT: Backend PDF Generation
 * -----------------------------------
 * When the "Generate PDF" checkbox is enabled (to be implemented) and the save
 * button is clicked, this component will send generate_pdf=true to the backend API.
 *
 * The backend then automatically generates a PDF in the API endpoint:
 * - Location: backend/webapp/tmlog/api.py (TMPriceViewSet)
 * - Method: create() or update() depending on operation
 * - PDF Generator: backend/webapp/tmlog/pdf_gen.py (generate_tm_pdf function)
 * - Format: City_DFA.pdf template (based on report_type field)
 * - Storage: Saved locally when DEBUG=True, uploaded to Egnyte when DEBUG=False
 * - Filename: TM-{job_number}-{id:03d}-PRICED.pdf
 *
 * PDF generation is CONDITIONAL - only happens when checkbox is ticked.
 * Does not generate automatically on every save.
 *
 * TODO: Add "Generate PDF" checkbox above save buttons and pass flag to API
 */
const TMPrice = () => {
  const navigate = useNavigate();
  const { getReportById, getPriceByReport, refreshReports } = useTMLogContext();
  const { reportId } = useParams();

  useEffect(() => {
    document.title = "T&M Report - R&S";
  }, []);

  const [scopes, setScopes] = useState(null);
  const [isLoadingScopes, setIsLoadingScopes] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Track when ref data has been loaded
  const [priceId, setPriceId] = useState(null); // Saved TMPrice id (null until first save)
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const loadedFromReport = useRef(false);
  const existingPricerSignature = useRef(null); // To load existing signature if editing

  // Hybrid form state management
  // State: Only fields that affect UI rendering (line_items with pricing, pricer_signature)
  // Refs: All read-only display fields + generate_pdf flag
  const {
    state: priceData,
    setState: setPriceData,
    refs,
    handleSubmit,
    isSubmitting,
  } = useFormHybrid({
    initialState: {
      line_items: [], // Used by TMTable for pricing display
      comments: "", // Used by Comments textarea
      pricer_signature: null, // Used by SignaturePad
      total_cost: 0, // Calculated from line_items, displayed
      // Editable percentages for summary table
      labor_markup_percentage: 0.35,
      equipment_markup_percentage: 0.15,
      material_markup_percentage: 0.15,
      material_tax_percentage: 0.0875,
      services_markup_percentage: 0.10,
      bi_percentage: 0.035,
    },
    initialRefs: {
      generate_pdf: true, // PDF generation checkbox - default to true to match defaultChecked={true}
      // All other fields loaded from loadPriceData() into refs
    },
    onSubmit: async (payload) => {
      if (!loadedFromReport.current) {
        await updateTMPrice(priceId || refs.current.id, payload);
      } else {
        const created = await createTMPrice(reportId, payload);
        if (created?.id) {
          setPriceId(created.id);
          refs.current.id = created.id;
          loadedFromReport.current = false;
        }
      }
      setHasUnsavedChanges(false);
      await refreshReports();

      // Only navigate back to TMLog when marking as priced
      if (payload.mark_as_priced || refs.current.mark_as_priced) {
        navigate("/operations/tm-log");
      }
    },
  });

  const loadPriceData = useCallback(async () => {
    try {
      const data = await getPriceByReport(reportId);

      if (data.status === 204) {  // No content
        // No price yet (expected for new reports): load original report to pre-populate form
        const reportData = await getReportById(reportId);

        setPriceData({
          line_items: reportData.line_items || [],
          comments: reportData.comments || "",
          pricer_signature: null,
          total_cost: 0,
          labor_markup_percentage: 0.35,
          equipment_markup_percentage: 0.15,
          material_markup_percentage: 0.15,
          material_tax_percentage: 0.0875,
          services_markup_percentage: 0.10,
          bi_percentage: 0.035,
        });

        Object.assign(refs.current, {
          tm_id: reportData.tm_id,
          job: reportData.job,
          job_number: reportData.job_number,
          job_description: reportData.job_description,
          date_performed: reportData.date_performed,
          description: reportData.description || "",
          report_type: reportData.report_type || "",
          pco_number: reportData.pco_number || "",
          contractor_name: reportData.contractor_name || "",
          pm_signature: reportData.pm_signature,
        });

        setPriceId(null);
        setHasUnsavedChanges(false);
        loadedFromReport.current = true;
      } else {
        // Existing price: load price record
        setPriceData({
          line_items: data.line_items || [],
          comments: data.comments || "",
          pricer_signature: data.pricer_signature,
          total_cost: data.total_cost || 0,
          labor_markup_percentage: data.labor_markup_percentage ?? 0.35,
          equipment_markup_percentage: data.equipment_markup_percentage ?? 0.15,
          material_markup_percentage: data.material_markup_percentage ?? 0.15,
          material_tax_percentage: data.material_tax_percentage ?? 0.0875,
          services_markup_percentage: data.services_markup_percentage ?? 0.10,
          bi_percentage: data.bi_percentage ?? 0.035,
        });

        Object.assign(refs.current, {
          id: data.id,
          tm_id: data.tm_id,
          job: data.job,
          job_number: data.job_number,
          job_description: data.job_description,
          date_performed: data.date_performed,
          description: data.description || "",
          report_type: data.report_type || "",
          pco_number: data.pco_number || "",
          contractor_name: data.contractor_name || "",
          pm_signature: data.pm_signature,
        });

        setPriceId(data.id);
        setHasUnsavedChanges(false);
        existingPricerSignature.current = data.pricer_signature;
        loadedFromReport.current = false;
      }

      setDataLoaded(true);
    } catch (error) {
      console.error("Error loading price or report data:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, getPriceByReport, getReportById, setPriceData]); // refs intentionally excluded - it's a stable ref object

  useEffect(() => {
    if (reportId) {
      loadPriceData();
    }
  }, [reportId, loadPriceData]);

  // Load scopes when data is loaded and date is available
  useEffect(() => {
    if (dataLoaded && refs.current?.date_performed) {
      /**
       * Loads dispatch scopes for the week of `date_performed` (line-item job context).
       *
       * @returns {Promise<void>}
       */
      const loadScopes = async () => {
        try {
          setIsLoadingScopes(true);
          const dateObj = getDateInLocalTZFromDateStr(
            refs.current.date_performed
          );
          const weekStart = getWeekStart(dateObj);
          const scopesData = await getScopesByWeek(weekStart);
          setScopes(scopesData);
        } catch (error) {
          console.error("Error loading scopes:", error);
        } finally {
          setIsLoadingScopes(false);
        }
      };
      loadScopes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]); // Re-run when data is loaded

  /**
   * Saves the price form, optionally flagging the report as priced.
   *
   * @param {React.FormEvent|undefined} e Submit event from useFormHybrid.
   * @param {boolean} [markAsPriced=false] Whether to set the priced flag.
   * @returns {Promise<void>}
   */
  const handleSave = async (e, markAsPriced = false) => {
    // Set flag to tell backend to update update_history when marking as priced
    // This is not a model field, just a flag like generate_pdf
    if (markAsPriced) {
      refs.current.mark_as_priced = true;
    } else {
      // Remove the flag for regular saves
      delete refs.current.mark_as_priced;
    }

    // Use the handleSubmit from useFormHybrid
    await handleSubmit(e);
  };

  /**
   * Returns to the T&M log without saving.
   *
   * @returns {void}
   */
  const handleCancel = () => {
    navigate("/operations/tm-log");
  };

  /**
   * Apply price form updates and mark the page as having unsaved changes.
   *
   * @param {Object|Function} next - Partial state object or updater function.
   * @returns {void}
   */
  const updatePriceData = (next) => {
    setPriceData(next);
    setHasUnsavedChanges(true);
  };

  // Handler to add new line items from AddLineItemButton
  /**
   * Appends a line item and marks the price form dirty.
   *
   * @param {Object} newItem New price line item.
   * @returns {void}
   */
  const handleAddItem = (newItem) => {
    updatePriceData({
      ...priceData,
      line_items: [...(priceData.line_items || []), newItem],
    });
  };

  // Create a minimal WeeklyContext value with only the scopes data needed
  const weeklyContextValue = useMemo(
    () => ({
      scopes,
      isLoading: isLoadingScopes,
    }),
    [scopes, isLoadingScopes]
  );

  /**
   * Sums line-item cost (labor uses RT/OT hours × rates; others use qty × rate).
   *
   * @param {Array<Object>} items Price line items.
   * @returns {number} Total cost.
   */
  const calculateTotalCost = (items) => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rtHrs = parseFloat(item.rt_hours) || 0;
      const otHrs = parseFloat(item.ot_hours) || 0;
      const rateRT = parseFloat(item.rate_rt || item.rate) || 0;
      const rateOT = parseFloat(item.rate_ot) || 0;

      let itemTotal = 0;
      if (item.type === "labor" || item.type === "custom_labor") {
        // Labor: RT Hours * RT Rate + OT Hours * OT Rate
        itemTotal = rtHrs * rateRT + otHrs * rateOT;
      } else if (item.type === "equipment" || item.type === "custom_equipment") {
        // Equipment: Quantity * (RT Hours * RT Rate + OT Hours * OT Rate)
        itemTotal = qty * (rtHrs * rateRT + otHrs * rateOT);
      } else {
        // Material, Concrete, Subcontractor, Dumping: Quantity * RT Rate
        itemTotal = qty * rateRT;
      }

      return Math.round((sum + itemTotal) * 100) / 100;
    }, 0);
  };

  return (
    <WeeklyContext.Provider value={weeklyContextValue}>
      <div className="p-2 sm:p-3 max-w-full sm:max-w-[1084px] mx-auto">
        <div className="bg-orange-600 text-white p-3 sm:p-4 rounded-t-lg">
          <div className="flex justify-between items-center gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Price T&M Tag</h1>
              <p className="text-sm">
                T&M ID: {refs.current.tm_id || "Loading..."}
              </p>
            </div>
            {priceId ? (
              <Button
                variant="custom"
                text="View PDF"
                onClick={() => setShowPdfModal(true)}
                className="px-3 py-1.5 text-sm font-semibold bg-white text-orange-800 hover:bg-orange-50 border border-orange-200"
              />
            ) : null}
          </div>
        </div>

        <div className="bg-white rounded-b-lg shadow p-3 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            <div className="border-b pb-4">
              <JobInformation
                data={refs.current}
                onChange={() => { }} // No-op, read-only
                readOnly={true}
              />
            </div>

            <div className="border-b pb-4">
              <ReportInformation
                key={`${reportId}-${dataLoaded}`} // Remount when data loads or report changes
                data={refs.current}
                onChange={() => { }} // No-op, read-only
                readOnly={true}
              />
            </div>

            <div className="border-b pb-4">
              <WorkDetail
                data={refs.current}
                onChange={() => { }} // No-op, read-only
                readOnly={true}
              />
            </div>

            <div className="">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700">
                  Cost Breakdown - Pricing
                </h2>
                <div className="flex space-x-2">
                  <div className="relative group">
                    <Button
                      variant="custom"
                      text="View In Admin"
                      onClick={() => {
                        window.open(
                          `/admin/tmlog/tmreport/${reportId}/change/`,
                          "_blank"
                        );
                      }}
                      className="h-[24px] text-sm font-medium bg-green-600 text-white rounded px-2"
                      disabled={isSubmitting}
                    />
                    {loadedFromReport.current && (
                      <TooltipArrow
                        text="Warning: Pricing data not yet saved and will not show in admin until saved."
                        position="above"
                      />
                    )}
                  </div>
                  <AddLineItemButton onAddItem={handleAddItem} />
                </div>
              </div>
              <div className="overflow-x-auto">
                <TMTable
                  items={priceData?.line_items || []}
                  onChange={(items) => {
                    updatePriceData({
                      ...priceData,
                      line_items: items,
                      total_cost: calculateTotalCost(items),
                    });
                  }}
                  showPricing={true}
                  loadedFromReport={loadedFromReport.current}
                />
              </div>
            </div>

            <div className="border-b pb-4">

              <SummaryTable
                lineItems={priceData?.line_items || []}
                percentages={{
                  labor_markup_percentage: priceData?.labor_markup_percentage,
                  equipment_markup_percentage: priceData?.equipment_markup_percentage,
                  material_markup_percentage: priceData?.material_markup_percentage,
                  material_tax_percentage: priceData?.material_tax_percentage,
                  services_markup_percentage: priceData?.services_markup_percentage,
                  bi_percentage: priceData?.bi_percentage,
                }}
                onPercentagesChange={(updatedPercentages) => {
                  updatePriceData({
                    ...priceData,
                    ...updatedPercentages,
                  });
                }}
              />
            </div>

            <Comments
              data={priceData}
              onChange={updatePriceData}
              maxLength={720}
              readOnly={false}
              title="Internal Comments"
              placeholder="Add internal comments or notes..."
            />

            <div className="border-b pb-4">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Price Preparer Signature
              </h2>
              <SignaturePad
                value={priceData?.pricer_signature}
                initialValue={existingPricerSignature.current}
                onChange={(signature) =>
                  updatePriceData({ ...priceData, pricer_signature: signature })
                }
                readOnly={false}
              />
            </div>
          </div>

          {/* PDF Generation Checkbox */}
          <div className="mt-6 mb-4 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
            <input
              type="checkbox"
              id="generate-pdf-price"
              name="generate_pdf"
              defaultChecked={true}
              data-ref="true"
              onChange={(e) => {
                refs.current.generate_pdf = e.target.checked;
              }}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="generate-pdf-price"
              className="text-sm font-medium text-gray-700"
            >
              Save to Egnyte
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <Button
              onClick={handleCancel}
              variant="custom"
              text="Cancel"
              className="flex-1 text-black bg-yellow-100 hover:bg-red-500 hover:text-white py-1.5 px-2"
            />
            <Button
              onClick={(e) => handleSave(e, false)}
              disabled={isSubmitting}
              variant="secondary"
              text={isSubmitting ? "Saving..." : "Save"}
              className="flex-1"
            />
            <Button
              onClick={(e) => handleSave(e, true)}
              disabled={isSubmitting}
              text={isSubmitting ? "Saving..." : "Save & Mark as Priced"}
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {priceId ? (
        <PdfViewerModal
          isOpen={showPdfModal}
          onClose={() => setShowPdfModal(false)}
          pdfUrl={getTMPricePdfUrl(priceId)}
          authenticated
          title="T&M Priced PDF"
          subtitle={
            hasUnsavedChanges
              ? "Save any changes before viewing — unsaved edits will not appear in this PDF."
              : undefined
          }
        />
      ) : null}
    </WeeklyContext.Provider>
  );
};

export default TMPrice;
